"use client"

import { useEffect, useState } from 'react'
import { useSubsStore } from '@/state/MsgTreeProvider'
import {
  activatePoo,
  loadOppRecipientEnrollment,
  resolveOppRecipient,
  savePooSetup,
  startOppRecipientEnrollment,
  type OppEnrollmentStatus,
  type OppRecipientResolution,
} from '../protocol.client'

export function PooSetupPanel() {
  const { state } = useSubsStore()
  const protocol = state.pooProtocol
  const [body, setBody] = useState(() => protocol?.fullContent?.body ?? '')
  const [preview, setPreview] = useState(() => protocol?.preview ?? '')
  const [recipientLookup, setRecipientLookup] = useState('')
  const [resolution, setResolution] = useState<OppRecipientResolution | null>(null)
  const [enrollment, setEnrollment] = useState<OppEnrollmentStatus | null>(null)
  const [evidenceProfileId, setEvidenceProfileId] = useState<'basic-internal' | 'strong-external'>('basic-internal')
  const [notifyRecipient, setNotifyRecipient] = useState(false)
  const [poaEnabled, setPoaEnabled] = useState(() => protocol?.poaEnabled ?? false)
  const [pending, setPending] = useState<'save' | 'resolve' | 'enroll' | 'status' | 'activate' | ''>('')
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  const legacy = protocol?.protocolVersion === 22
  useEffect(() => {
    if (!protocol || protocol.lifecycleState !== 'setup' || legacy) return
    let active = true
    const refresh = async () => {
      try {
        const next = await loadOppRecipientEnrollment(protocol)
        if (!active) return
        setEnrollment(next)
        const enrollmentLookup = next.recipientLookup || recipientLookup.trim()
        if (!recipientLookup.trim() && next.recipientLookup) setRecipientLookup(next.recipientLookup)
        if (next.state === 'completed' && enrollmentLookup) {
          const resolvedRecipient = await resolveOppRecipient(protocol, enrollmentLookup)
          if (active) setResolution(resolvedRecipient)
        }
      } catch {
        // Enrollment status is supplementary to the editable setup. Explicit
        // sender actions surface request errors below.
      }
    }
    void refresh()
    const interval = window.setInterval(() => {
      if (enrollment?.state === 'pending') void refresh()
    }, 10_000)
    return () => {
      active = false
      window.clearInterval(interval)
    }
  }, [legacy, protocol, recipientLookup, enrollment?.state])

  if (!protocol || protocol.lifecycleState !== 'setup') return null
  if (!protocol.allowedActions.activate || !protocol.fullContent) {
    return (
      <section className="mb-6 rounded-xl border border-[#d7d1c5] bg-white p-6 shadow-[0_1px_4px_rgba(28,40,36,0.07)]">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#68746f]">OPP setup</div>
        <h1 className="mt-1 text-xl font-semibold text-[#1d2a26]">This OPP document has not started</h1>
        <p className="mt-2 text-sm leading-6 text-[#68716d]">Only its sender can edit the draft, choose the recipient, and start OPP.</p>
      </section>
    )
  }

  const resolved = resolution?.status === 'ready' ? resolution : null
  const strongReady = Boolean(
    resolved
    && resolved.party.externalEmailReady
    && resolved.senderExternalEmailReady,
  )

  const resolveRecipient = async () => {
    setPending('resolve')
    setError('')
    setResolution(null)
    try {
      setResolution(await resolveOppRecipient(protocol, recipientLookup.trim()))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setPending('')
    }
  }

  const beginEnrollment = async () => {
    setPending('enroll')
    setError('')
    try {
      setEnrollment(await startOppRecipientEnrollment(protocol, recipientLookup.trim()))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setPending('')
    }
  }

  const refreshEnrollment = async () => {
    setPending('status')
    setError('')
    try {
      const next = await loadOppRecipientEnrollment(protocol)
      setEnrollment(next)
      if (next.state === 'completed') setResolution(await resolveOppRecipient(protocol, recipientLookup.trim()))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setPending('')
    }
  }

  const submit = async (kind: 'save' | 'activate') => {
    if (kind === 'activate') {
      if (legacy) {
        const confirmedRecipientEmail = recipientLookup.trim().toLowerCase()
        if (!window.confirm(
          `Start OPP for ${confirmedRecipientEmail}?\n\nThis permanently freezes the document and recipient. A mistake requires a new document.`,
        )) return
      } else {
        if (!resolved) {
          setError('Resolve and confirm the MsgDesk recipient before starting OPP.')
          return
        }
        if (evidenceProfileId === 'strong-external' && !strongReady) {
          setError('Strong OPP requires active verified external mailboxes for both parties.')
          return
        }
        if (!window.confirm(
          `Start ${evidenceProfileId === 'strong-external' ? 'Strong' : 'Basic'} OPP for @${resolved.party.displayName} (${resolved.party.msgdeskAddress})?\n\nThis permanently freezes the document, both MsgDesk parties, and the evidence profile. A change requires a new document.`,
        )) return
      }
    }

    setPending(kind)
    setError('')
    setSaved(false)
    try {
      if (kind === 'save') {
        await savePooSetup(protocol, { body, preview, poaEnabled })
        setSaved(true)
      } else if (legacy) {
        await activatePoo(protocol, {
          body,
          preview,
          recipientEmail: recipientLookup.trim().toLowerCase(),
          poaEnabled,
        })
      } else if (resolved) {
        await activatePoo(protocol, {
          body,
          preview,
          recipientLookup: recipientLookup.trim(),
          expectedRecipientId: resolved.party.partyId,
          expectedRecipientControllerId: resolved.party.controllerId,
          evidenceProfileId,
          notifyRecipient,
          poaEnabled: evidenceProfileId === 'strong-external' ? true : poaEnabled,
        })
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setPending('')
    }
  }

  return (
    <section data-md-contract="poo.setup" className="mb-6 overflow-hidden rounded-xl border border-[#bfcfc8] bg-white shadow-[0_1px_4px_rgba(28,40,36,0.07)]">
      <header className="border-b border-[#dbe5e0] bg-[#eaf2ee] px-5 py-4 sm:px-7">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#557168]">Step 0 · editable draft · OPP v{protocol.protocolVersion}</div>
        <h1 className="mt-1 text-2xl font-semibold text-[#1d2a26]">Prepare this OPP document</h1>
        <p className="mt-1 max-w-3xl text-sm leading-6 text-[#596660]">Edit the text and public preview. Starting OPP permanently freezes the document, parties, and evidence profile.</p>
      </header>
      <div className="space-y-5 px-5 py-5 sm:px-7 sm:py-6">
        {error ? <p role="alert" className="rounded-md border border-[#b64d46] bg-[#fff5f4] px-4 py-3 text-sm text-[#77342f]">{error}</p> : null}
        {saved ? <p role="status" className="rounded-md border border-[#9fc4b4] bg-[#f1f8f5] px-4 py-3 text-sm text-[#315f52]">Draft saved.</p> : null}
        <label className="block text-sm font-semibold text-[#26332f]">
          Document text
          <textarea required value={body} onChange={event => setBody(event.target.value)} className="mt-2 min-h-64 w-full rounded-lg border border-[#c9c4b9] bg-white p-4 font-normal leading-7 text-[#34413c] outline-none focus:border-[#557f70]" />
        </label>
        <label className="block text-sm font-semibold text-[#26332f]">
          Public preview
          <textarea required value={preview} onChange={event => setPreview(event.target.value)} className="mt-2 min-h-24 w-full rounded-lg border border-[#c9c4b9] bg-white p-3 font-normal leading-6 text-[#34413c] outline-none focus:border-[#557f70]" />
        </label>

        {legacy ? (
          <label className="block text-sm font-semibold text-[#26332f]">
            Recipient email
            <input type="email" required value={recipientLookup} onChange={event => setRecipientLookup(event.target.value)} placeholder="recipient@example.com" className="mt-2 h-11 w-full rounded-lg border border-[#c9c4b9] bg-white px-3 font-normal text-[#34413c] outline-none focus:border-[#557f70]" />
          </label>
        ) : (
          <div className="rounded-xl border border-[#d5ddd9] bg-[#f8faf9] p-4">
            <label className="block text-sm font-semibold text-[#26332f]">
              Recipient MsgDesk identity
              <span className="mt-1 block text-xs font-normal leading-5 text-[#66726d]">Enter a namekey, codekey, MsgDesk address, or an external email already bound to a MsgDesk message.</span>
              <span className="mt-2 flex flex-col gap-2 sm:flex-row">
                <input
                  required
                  value={recipientLookup}
                  onChange={event => {
                    setRecipientLookup(event.target.value)
                    setResolution(null)
                    setEnrollment(null)
                  }}
                  placeholder="@namekey or codekey@msgdesk.com"
                  className="h-11 min-w-0 flex-1 rounded-lg border border-[#c9c4b9] bg-white px-3 font-normal text-[#34413c] outline-none focus:border-[#557f70]"
                />
                <button type="button" disabled={Boolean(pending) || !recipientLookup.trim()} onClick={() => void resolveRecipient()} className="h-11 rounded-md border border-[#557f70] bg-white px-5 text-sm font-semibold text-[#315f52] disabled:opacity-50">
                  {pending === 'resolve' ? 'Resolving…' : 'Resolve recipient'}
                </button>
              </span>
            </label>
            {resolved ? (
              <div role="status" className="mt-3 rounded-md border border-[#9fc4b4] bg-white px-4 py-3 text-sm text-[#315f52]">
                <strong>@{resolved.party.displayName}</strong> · {resolved.party.msgdeskAddress}
                <span className="mt-1 block text-xs text-[#5d6b65]">Frozen party: {resolved.party.partyId}</span>
              </div>
            ) : resolution?.status === 'needs-enrollment' ? (
              <div role="status" className="mt-3 rounded-md border border-[#d7c89f] bg-[#fffaf0] px-4 py-3 text-sm text-[#6d592f]">
                <p>This address has no verified MsgDesk message identity yet. The recipient must verify it and create a full MsgDesk message before OPP can start.</p>
                {enrollment?.state === 'pending' ? (
                  <p className="mt-2 text-xs">Enrollment {enrollment.deliveryState}. Expires {enrollment.expiresAt ? new Date(enrollment.expiresAt * 1000).toLocaleString() : 'soon'}{enrollment.deliveryError ? ` · ${enrollment.deliveryError}` : ''}.</p>
                ) : null}
                <span className="mt-3 flex flex-wrap gap-2">
                  <button type="button" disabled={Boolean(pending)} onClick={() => void beginEnrollment()} className="h-10 rounded-md bg-[#785a22] px-4 text-xs font-semibold text-white disabled:opacity-50">{pending === 'enroll' ? 'Sending…' : enrollment?.state === 'pending' ? 'Replace enrollment invitation' : 'Send enrollment invitation'}</button>
                  <button type="button" disabled={Boolean(pending)} onClick={() => void refreshEnrollment()} className="h-10 rounded-md border border-[#9a8660] bg-white px-4 text-xs font-semibold text-[#675630] disabled:opacity-50">{pending === 'status' ? 'Checking…' : 'Check enrollment status'}</button>
                </span>
              </div>
            ) : resolution ? (
              <p role="alert" className="mt-3 rounded-md border border-[#d6b0ab] bg-[#fff7f6] px-4 py-3 text-sm text-[#713d38]">The recipient is {resolution.status.replace('-', ' ')}. Use an unambiguous active MsgDesk identity.</p>
            ) : null}
          </div>
        )}

        {!legacy ? (
          <fieldset className="rounded-xl border border-[#d5ddd9] p-4">
            <legend className="px-1 text-sm font-semibold text-[#26332f]">Evidence profile</legend>
            <label className="mt-2 flex items-start gap-3 text-sm text-[#34413c]">
              <input type="radio" name="opp-profile" checked={evidenceProfileId === 'basic-internal'} onChange={() => setEvidenceProfileId('basic-internal')} className="mt-1" />
              <span><strong>Basic.</strong> Internal MsgDesk identity, a user-held secret, signed history, and external anchoring. External email is optional notification only.</span>
            </label>
            <label className="mt-3 flex items-start gap-3 text-sm text-[#34413c]">
              <input type="radio" name="opp-profile" checked={evidenceProfileId === 'strong-external'} onChange={() => {
                setEvidenceProfileId('strong-external')
                setPoaEnabled(true)
              }} className="mt-1" />
              <span><strong>Strong.</strong> Adds independently authenticated external-mailbox attestations for both parties and requires explicit Acceptance Proof.</span>
            </label>
            {evidenceProfileId === 'strong-external' && resolved ? (
              <p className={`mt-3 text-xs font-semibold ${strongReady ? 'text-[#315f52]' : 'text-[#8a4942]'}`}>
                Strong mailbox readiness: sender {resolved.senderExternalEmailReady ? 'ready' : 'not ready'} · recipient {resolved.party.externalEmailReady ? 'ready' : 'not ready'}
              </p>
            ) : null}
          </fieldset>
        ) : null}

        {!legacy && evidenceProfileId === 'basic-internal' ? (
          <label className="flex items-start gap-3 text-sm text-[#34413c]">
            <input type="checkbox" checked={notifyRecipient} onChange={event => setNotifyRecipient(event.target.checked)} disabled={!resolved?.party.externalEmailReady} className="mt-1" />
            <span><strong className="font-semibold">Send an optional external notification.</strong> It is a MsgDesk receipt, not independent recipient attestation.</span>
          </label>
        ) : null}
        <label className="flex items-start gap-3 text-sm text-[#34413c]">
          <input type="checkbox" checked={poaEnabled} disabled={!legacy && evidenceProfileId === 'strong-external'} onChange={event => setPoaEnabled(event.target.checked)} className="mt-1" />
          <span><strong className="font-semibold">Require an Acceptance Proof.</strong> The recipient must explicitly accept after opening.</span>
        </label>
        <div className="flex flex-wrap justify-end gap-3 border-t border-[#e6e1d8] pt-5">
          <button type="button" disabled={Boolean(pending)} onClick={() => void submit('save')} className="h-11 rounded-md border border-[#7f948b] bg-white px-5 text-sm font-semibold text-[#315f52] disabled:opacity-50">{pending === 'save' ? 'Saving…' : 'Save draft'}</button>
          <button type="button" disabled={Boolean(pending) || (legacy ? !recipientLookup.trim() : !resolved)} onClick={() => void submit('activate')} className="h-11 rounded-md bg-[#315f52] px-6 text-sm font-semibold text-white enabled:hover:bg-[#264c42] disabled:cursor-not-allowed disabled:bg-[#d9dedb] disabled:text-[#707a76]">{pending === 'activate' ? 'Starting…' : 'Start OPP'}</button>
        </div>
      </div>
    </section>
  )
}
